import React from 'react';
import styled from 'styled-components';
import Header from '../components/Shared/Header';
import Footer from '../components/Shared/Footer';
import { PageContainer } from '../styles/PublicPage.styles';

const ContentWrapper = styled.div`
  max-width: 600px;
  margin: 0 auto;
  background: white;
  padding: 2rem;
  border-radius: 12px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.05);
`;

const Title = styled.h1`
  color: #1a365d;
  font-size: 2rem;
  margin-bottom: 1.5rem;
  text-align: center;
`;

const WarningBox = styled.div`
  background: #fff5f5;
  border: 1px solid #feb2b2;
  border-radius: 8px;
  padding: 1rem;
  margin-bottom: 2rem;
`;

const WarningText = styled.p`
  color: #c53030;
  font-size: 1rem;
  line-height: 1.5;
`;

const Section = styled.div`
  margin-bottom: 2rem;
`;

const SectionTitle = styled.h2`
  color: #2d3748;
  font-size: 1.25rem;
  margin-bottom: 1rem;
`;

const Text = styled.p`
  color: #4a5568;
  line-height: 1.6;
  margin-bottom: 1rem;
`;

const List = styled.ul`
  list-style-type: disc;
  margin-left: 1.5rem;
  color: #4a5568;
  margin-bottom: 1rem;
`;

const ListItem = styled.li`
  margin-bottom: 0.5rem;
`;

const SupportBox = styled.div`
  background: #ebf8ff;
  border: 1px solid #bee3f8;
  border-radius: 8px;
  padding: 1.5rem;
  margin-bottom: 2rem;
`;

const SupportText = styled.p`
  color: #2b6cb0;
  font-size: 1rem;
  line-height: 1.6;
  margin-bottom: 1rem;
`;

const EmailLink = styled.a`
  color: #2b6cb0;
  text-decoration: none;
  font-weight: 500;

  &:hover {
    text-decoration: underline;
  }
`;

const DeleteAccount: React.FC = () => {
  return (
    <PageContainer>
      <Header />
      <ContentWrapper>
        <Title>Request Account Deletion</Title>
        
        <WarningBox>
          <WarningText>
            ⚠️ Warning: Account deletion is a permanent action that cannot be undone.
          </WarningText>
        </WarningBox>

        <Section>
          <SectionTitle>What happens when your account is deleted:</SectionTitle>
          <List>
            <ListItem>All your personal information will be permanently deleted</ListItem>
            <ListItem>Your learning progress and achievements will be erased</ListItem>
            <ListItem>Your account settings and preferences will be removed</ListItem>
            <ListItem>You will lose access to all purchased content</ListItem>
          </List>
        </Section>

        <Section>
          <SectionTitle>Data Retention</SectionTitle>
          <Text>
            After account deletion, we may retain certain information as required by law or for legitimate business purposes. This includes:
          </Text>
          <List>
            <ListItem>Transaction records for financial compliance</ListItem>
            <ListItem>Communication logs for legal purposes</ListItem>
            <ListItem>Aggregated usage statistics (non-personal)</ListItem>
          </List>
        </Section>

        <SupportBox>
          <SectionTitle>How to Request Account Deletion</SectionTitle>
          <SupportText>
            To request account deletion, please send an email to our support team at{' '}
            <EmailLink href="mailto:support@q3learners.com?subject=DELETE%20ACCOUNT">
              support@q3learners.com
            </EmailLink>
          </SupportText>
          <SupportText>
            Please include the following information in your email:
          </SupportText>
          <List>
            <ListItem>Your full name</ListItem>
            <ListItem>Your registered email address</ListItem>
            <ListItem>Your username (if different from email)</ListItem>
            <ListItem>Subject line must be: "DELETE ACCOUNT"</ListItem>
          </List>
          <SupportText>
            Our support team will process your request within 5-7 business days and send you a confirmation email once your account has been deleted.
          </SupportText>
        </SupportBox>

        <Text style={{ fontSize: "14px", textAlign: "center" }}>
          If you signed up with Google or Apple, you can also revoke access from their settings.
        </Text>
      </ContentWrapper>
      <Footer />
    </PageContainer>
  );
};

export default DeleteAccount; 